import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheckIcon,
  UserGroupIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const YesPartialNoRadio = ({ name, value, onChange, className = '' }) => {
  const options = [
    { value: 'yes', label: 'Yes', color: 'text-green-600' },
    { value: 'partial', label: 'Partial', color: 'text-yellow-600' },
    { value: 'no', label: 'No', color: 'text-red-600' },
  ];

  return (
    <div className={`flex gap-4 ${className}`}>
      {options.map(option => (
        <label key={option.value} className="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={e => onChange(e.target.value)}
            className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-blue-500"
          />
          <span className={`text-sm font-medium ${option.color}`}>{option.label}</span>
        </label>
      ))}
    </div>
  );
};

const GovernanceInclusionAssessment = ({ data = {}, onSave, userRole }) => {
  const [assessmentData, setAssessmentData] = useState({
    equityInclusion: {
      youthInclusion: data.equityInclusion?.youthInclusion || 'no',
      womenInclusion: data.equityInclusion?.womenInclusion || 'no',
      ruralInclusion: data.equityInclusion?.ruralInclusion || 'no',
      disabledInclusion: data.equityInclusion?.disabledInclusion || 'no',
      socioeconomicSupport: data.equityInclusion?.socioeconomicSupport || 'no',
      notes: data.equityInclusion?.notes || '',
    },
    ethicalStandards: {
      dataProtection: data.ethicalStandards?.dataProtection || 'no',
      gdprCompliance: data.ethicalStandards?.gdprCompliance || 'no',
      privacyPolicy: data.ethicalStandards?.privacyPolicy || 'no',
      consentManagement: data.ethicalStandards?.consentManagement || 'no',
      termsOfService: data.ethicalStandards?.termsOfService || 'no',
      notes: data.ethicalStandards?.notes || '',
    },
    riskManagement: {
      dropoutRisk: data.riskManagement?.dropoutRisk || 'no',
      coachAvailability: data.riskManagement?.coachAvailability || 'no',
      underperformance: data.riskManagement?.underperformance || 'no',
      disputeResolution: data.riskManagement?.disputeResolution || 'no',
      grievanceMechanisms: data.riskManagement?.grievanceMechanisms || 'no',
      notes: data.riskManagement?.notes || '',
    },
    score: data.score || 0,
    notes: data.notes || '',
  });

  const [saving, setSaving] = useState(false);

  const calculateScore = () => {
    let totalPoints = 0;
    let maxPoints = 0;

    const getPoints = value => {
      if (value === 'yes') return 1;
      if (value === 'partial') return 0.5;
      return 0;
    };

    const equityInclusion = assessmentData.equityInclusion;
    totalPoints += getPoints(equityInclusion.youthInclusion);
    totalPoints += getPoints(equityInclusion.womenInclusion);
    totalPoints += getPoints(equityInclusion.ruralInclusion);
    totalPoints += getPoints(equityInclusion.disabledInclusion);
    totalPoints += getPoints(equityInclusion.socioeconomicSupport);
    maxPoints += 5;

    const ethicalStandards = assessmentData.ethicalStandards;
    totalPoints += getPoints(ethicalStandards.dataProtection);
    totalPoints += getPoints(ethicalStandards.gdprCompliance);
    totalPoints += getPoints(ethicalStandards.privacyPolicy);
    totalPoints += getPoints(ethicalStandards.consentManagement);
    totalPoints += getPoints(ethicalStandards.termsOfService);
    maxPoints += 5;

    const riskManagement = assessmentData.riskManagement;
    totalPoints += getPoints(riskManagement.dropoutRisk);
    totalPoints += getPoints(riskManagement.coachAvailability);
    totalPoints += getPoints(riskManagement.underperformance);
    totalPoints += getPoints(riskManagement.disputeResolution);
    totalPoints += getPoints(riskManagement.grievanceMechanisms);
    maxPoints += 5;

    return Math.round((totalPoints / maxPoints) * 100);
  };

  const handleSave = async () => {
    setSaving(true);
    const updatedData = {
      ...assessmentData,
      score: calculateScore(),
      lastUpdated: new Date().toISOString(),
    };

    try {
      await onSave(updatedData);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const getScoreColor = score => {
    if (score >= 81) return 'text-green-600';
    if (score >= 61) return 'text-yellow-600';
    if (score >= 41) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreStatus = score => {
    if (score >= 81) return 'Healthy & Scalable';
    if (score >= 61) return 'Emerging Maturity';
    if (score >= 41) return 'At Risk';
    return 'Critical Gaps';
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-red-200 bg-red-50 p-6">
        <div className="mb-4 flex items-center gap-3">
          <ShieldCheckIcon className="h-8 w-8 text-red-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Governance, Inclusion & Risk Management
            </h2>
            <p className="text-gray-600">
              Assess governance structures, equity & inclusion practices, and risk management
              systems
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600">Current Score</div>
            <div className={`text-3xl font-bold ${getScoreColor(calculateScore())}`}>
              {calculateScore()}%
            </div>
            <div className="text-sm text-gray-600">{getScoreStatus(calculateScore())}</div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-red-600 px-6 py-3 font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Assessment'}
          </button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <UserGroupIcon className="h-6 w-6 text-blue-600" />
          <h3 className="text-xl font-semibold text-gray-900">Equity & Inclusion</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program address youth inclusion?
            </label>
            <p className="text-xs text-gray-600">
              Specific measures and support systems to ensure meaningful participation of young
              entrepreneurs
            </p>
            <YesPartialNoRadio
              name="youthInclusion"
              value={assessmentData.equityInclusion.youthInclusion}
              onChange={value => updateField('equityInclusion', 'youthInclusion', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program address women inclusion?
            </label>
            <p className="text-xs text-gray-600">
              Gender-sensitive approaches and support mechanisms to promote women's entrepreneurship
            </p>
            <YesPartialNoRadio
              name="womenInclusion"
              value={assessmentData.equityInclusion.womenInclusion}
              onChange={value => updateField('equityInclusion', 'womenInclusion', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program address rural inclusion?
            </label>
            <p className="text-xs text-gray-600">
              Specific strategies to reach and support entrepreneurs in rural and remote areas
            </p>
            <YesPartialNoRadio
              name="ruralInclusion"
              value={assessmentData.equityInclusion.ruralInclusion}
              onChange={value => updateField('equityInclusion', 'ruralInclusion', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program address inclusion for people with disabilities?
            </label>
            <p className="text-xs text-gray-600">
              Accessibility measures and inclusive practices to support entrepreneurs with
              disabilities
            </p>
            <YesPartialNoRadio
              name="disabledInclusion"
              value={assessmentData.equityInclusion.disabledInclusion}
              onChange={value => updateField('equityInclusion', 'disabledInclusion', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are support mechanisms adapted for different socioeconomic backgrounds?
            </label>
            <p className="text-xs text-gray-600">
              Tailored support approaches that address barriers faced by entrepreneurs from
              disadvantaged backgrounds
            </p>
            <YesPartialNoRadio
              name="socioeconomicSupport"
              value={assessmentData.equityInclusion.socioeconomicSupport}
              onChange={value => updateField('equityInclusion', 'socioeconomicSupport', value)}
            />
          </div>

          <div>
            <label
              htmlFor="equityInclusionNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="equityInclusionNotes"
              value={assessmentData.equityInclusion.notes}
              onChange={e => updateField('equityInclusion', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <LockClosedIcon className="h-6 w-6 text-green-600" />
          <h3 className="text-xl font-semibold text-gray-900">
            Ethical Standards & Data Protection
          </h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are personal and business data protected?
            </label>
            <p className="text-xs text-gray-600">
              Comprehensive data protection measures aligned with privacy regulations and best
              practices
            </p>
            <YesPartialNoRadio
              name="dataProtection"
              value={assessmentData.ethicalStandards.dataProtection}
              onChange={value => updateField('ethicalStandards', 'dataProtection', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is the program aligned with GDPR and Data Protection Acts?
            </label>
            <p className="text-xs text-gray-600">
              Compliance with international and local data protection regulations
            </p>
            <YesPartialNoRadio
              name="gdprCompliance"
              value={assessmentData.ethicalStandards.gdprCompliance}
              onChange={value => updateField('ethicalStandards', 'gdprCompliance', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are privacy policies clearly defined and accessible?
            </label>
            <p className="text-xs text-gray-600">
              Clear, comprehensive, and easily accessible privacy policies for all stakeholders
            </p>
            <YesPartialNoRadio
              name="privacyPolicy"
              value={assessmentData.ethicalStandards.privacyPolicy}
              onChange={value => updateField('ethicalStandards', 'privacyPolicy', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is consent clearly managed and documented?
            </label>
            <p className="text-xs text-gray-600">
              Robust consent management systems with clear documentation and opt-in/opt-out
              mechanisms
            </p>
            <YesPartialNoRadio
              name="consentManagement"
              value={assessmentData.ethicalStandards.consentManagement}
              onChange={value => updateField('ethicalStandards', 'consentManagement', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are terms of service clearly defined?
            </label>
            <p className="text-xs text-gray-600">
              Comprehensive terms of service that clearly outline rights, responsibilities, and
              expectations
            </p>
            <YesPartialNoRadio
              name="termsOfService"
              value={assessmentData.ethicalStandards.termsOfService}
              onChange={value => updateField('ethicalStandards', 'termsOfService', value)}
            />
          </div>

          <div>
            <label
              htmlFor="ethicalStandardsNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="ethicalStandardsNotes"
              value={assessmentData.ethicalStandards.notes}
              onChange={e => updateField('ethicalStandards', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
          <h3 className="text-xl font-semibold text-gray-900">Risk Management</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are there systems in place to manage dropout risk?
            </label>
            <p className="text-xs text-gray-600">
              Early warning systems and intervention strategies to prevent and address participant
              dropout
            </p>
            <YesPartialNoRadio
              name="dropoutRisk"
              value={assessmentData.riskManagement.dropoutRisk}
              onChange={value => updateField('riskManagement', 'dropoutRisk', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are there systems in place to manage coach/mentor availability?
            </label>
            <p className="text-xs text-gray-600">
              Backup systems and capacity management to ensure consistent coaching support
            </p>
            <YesPartialNoRadio
              name="coachAvailability"
              value={assessmentData.riskManagement.coachAvailability}
              onChange={value => updateField('riskManagement', 'coachAvailability', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are there systems in place to manage underperformance?
            </label>
            <p className="text-xs text-gray-600">
              Performance monitoring and improvement strategies for participants who are not meeting
              expectations
            </p>
            <YesPartialNoRadio
              name="underperformance"
              value={assessmentData.riskManagement.underperformance}
              onChange={value => updateField('riskManagement', 'underperformance', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are there dispute resolution mechanisms available?
            </label>
            <p className="text-xs text-gray-600">
              Clear processes for handling conflicts and disputes between participants and program
              staff
            </p>
            <YesPartialNoRadio
              name="disputeResolution"
              value={assessmentData.riskManagement.disputeResolution}
              onChange={value => updateField('riskManagement', 'disputeResolution', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are there client grievance mechanisms available?
            </label>
            <p className="text-xs text-gray-600">
              Accessible channels for participants to raise concerns and complaints about program
              services
            </p>
            <YesPartialNoRadio
              name="grievanceMechanisms"
              value={assessmentData.riskManagement.grievanceMechanisms}
              onChange={value => updateField('riskManagement', 'grievanceMechanisms', value)}
            />
          </div>

          <div>
            <label
              htmlFor="riskManagementNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="riskManagementNotes"
              value={assessmentData.riskManagement.notes}
              onChange={e => updateField('riskManagement', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-orange-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h3 className="mb-4 text-xl font-semibold text-gray-900">Overall Assessment Notes</h3>
        <textarea
          value={assessmentData.notes}
          onChange={e => setAssessmentData(prev => ({ ...prev, notes: e.target.value }))}
          rows={4}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:ring-red-500"
          placeholder="Add any overall observations, recommendations, or strategic insights..."
        />
      </motion.div>
    </div>
  );
};

export default GovernanceInclusionAssessment;
